// src/navigation/NavigationModeDetector.jsx
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import VisitorNavigationWrapper from './wrappers/VisitorNavigationWrapper';
import PublicNavigationWrapper from './wrappers/PublicNavigationWrapper';
import ReaderNavigationWrapper from './wrappers/ReaderNavigationWrapper';
import CreatorNavigationWrapper from './wrappers/CreatorNavigationWrapper';
import AdminNavigationWrapper from './wrappers/AdminNavigationWrapper';

const NavigationModeDetector = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { pathname } = useLocation();

  // Helper function to check if user has a specific role
  const hasRole = (role) => {
    if (!user || !user.roles) return false;
    return user.roles.includes(role);
  };

  // Determine navigation mode based on authentication, role, and route
  const getNavigationMode = () => {
    // Special routes that should not have navigation wrappers
    // (These routes either have no nav, or have embedded nav that hasn't been migrated yet)
    const noNavRoutes = [
      '/login',
      '/register', 
      '/forgot-password',
      // Kiosk Module — full-screen immersive kiosk experience (no chrome)
      '/kiosk',
      // Marketing pages with embedded navigation (Phase 1):
      '/',
      '/about',
      '/pricing',
      '/faq',
      '/contact',
      '/authors',
      '/public/library',
      // Help Center with embedded navigation:
      '/help',
      // Author pages with embedded navigation (canonical):
      '/author',
      '/author-console',
      '/author-analytics',
      '/author/analytics',
      '/author/promotions',
      '/author/author-profile',
      // Creator pages with embedded navigation (legacy aliases):
      '/creator',
      '/creator-console',
      '/creator-analytics',
      '/creator/analytics',
      '/creator/promotions',
      // Publisher pages with embedded navigation:
      '/publisher',
      '/publisher/profile',
      '/publisher/authors',
      '/publisher/authors/new',
      '/publisher/titles',
      // Admin pages with embedded navigation (Phase 4):
      '/admin',
      '/admin/users',
      '/admin/books',
      '/admin/authors',
      '/admin/system',
      // Reader pages with embedded navigation (Phase 2):
      '/home',
      '/library',
      '/reader/my-library',
      // Standalone pages (no navigation):
      '/player',  // Manuscript player - immersive, standalone experience
      // Other:
      '/account'
    ];
    if (noNavRoutes.some(route => pathname === route || pathname.startsWith(route + '/'))) {
      return 'none';
    }

    // Authenticated users
    if (isAuthenticated && user) {
      // Admin routes and admin role
      if (pathname.startsWith('/admin') || hasRole('admin')) {
        // Admin has highest priority
        if (pathname.startsWith('/admin')) {
          return 'admin';
        }
        // If admin but on creator routes, show creator nav
        if (pathname.startsWith('/creator')) {
          return 'creator';
        }
        // If admin but on reader routes, show reader nav
        if (pathname === '/home' || pathname.startsWith('/library') || pathname.startsWith('/reader')) {
          return 'reader';
        }
        // Default to admin for authenticated admins
        return 'admin';
      }

      // Author/Creator routes and author/creator role
      // Note: "author" is the canonical role, "creator" is legacy
      if (pathname.startsWith('/author') || pathname.startsWith('/creator') || hasRole('author') || hasRole('creator')) {
        return 'creator'; // Use creator navigation wrapper for both
      }

      // Reader routes (authenticated library, home, etc.)
      // Note: /player is excluded - it's in noNavRoutes for standalone immersive experience
      if (pathname === '/home' || pathname === '/library' || pathname.startsWith('/reader')) {
        return 'reader';
      }

      // Marketing pages when authenticated show Reader nav (context-aware)
      if (pathname === '/' || pathname === '/about' || pathname === '/pricing' || pathname === '/faq' || pathname === '/contact') {
        return 'reader';
      }

      // Account page - use reader nav for authenticated users
      if (pathname === '/account') {
        return 'reader';
      }

      // Default for authenticated users
      return 'reader';
    }

    // Unauthenticated users
    if (!isAuthenticated) {
      // Public Library
      if (pathname.startsWith('/public/library') || pathname === '/public/library') {
        return 'public';
      }

      // All other unauthenticated routes use Visitor nav
      return 'visitor';
    }

    // Fallback
    return 'visitor';
  };

  const mode = getNavigationMode();

  // Routes with no navigation wrapper
  if (mode === 'none') {
    return <>{children}</>;
  }

  // Render appropriate wrapper based on detected mode
  switch (mode) {
    case 'admin':
      return <AdminNavigationWrapper>{children}</AdminNavigationWrapper>;
    case 'creator':
      return <CreatorNavigationWrapper>{children}</CreatorNavigationWrapper>;
    case 'reader':
      return <ReaderNavigationWrapper>{children}</ReaderNavigationWrapper>;
    case 'public':
      return <PublicNavigationWrapper>{children}</PublicNavigationWrapper>;
    case 'visitor':
    default:
      return <VisitorNavigationWrapper>{children}</VisitorNavigationWrapper>;
  }
};

export default NavigationModeDetector;
